/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2022
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/
import React, { useState, useEffect, useMemo } from 'react';

import { getAPIType } from 'utils/APITypes';
import ApiHandlerProvider from 'contexts/ApiHandler';
import { useCreateApiHandler } from 'hooks/createApiHandler';
import { Route, useParams } from 'react-router-dom';
import Routing from 'components/Routing';
import ExplorerBody from 'components/ExplorerBody';
import { apiPath, getApi } from 'utils/navigation';
import { useConfig } from 'contexts/config';
import { InlineLoading, ToastNotification } from 'carbon-components-react';
import { useTranslation } from 'react-i18next';
import { decycle } from 'json-decycle';

// This disables the react-hooks/rules-of-hooks linting check because it is incorrectly flagging issues using
// use* hooks inside callbacks. They are actually being used correctly, in components defined under the Routes
// but the linter isn't able to determine this.
/* eslint react-hooks/rules-of-hooks: 0 */

const getFromOptions = (entry, api, apis, options) => {
  let result = null;
  if (api && apis && options && options[entry]) {
    let apiIndex = apis.indexOf(api);
    result = options[entry][apiIndex] || null;
  }
  return result;
};

const buildApiState = (apis = [], options, api = apis[0]) => ({
  currentApi: JSON.parse(JSON.stringify(api, decycle())),
  apiType: getAPIType(api),
  infoSectionEntries: getFromOptions('infoSectionEntries', api, apis, options),
  documentationEntries: getFromOptions('documentationEntries', api, apis, options)
});

const App = () => {
  return (
    <Route
      path={['/:entity/*', '/']}
      component={({ location }) => {
        const pathParams = useParams();

        const entityIsApi = useMemo(() => pathParams.entity && pathParams.entity !== 'documentation', [pathParams]);
        const { apis, options } = useConfig();
        const [apiState, updateApiState] = useState(() => buildApiState(apis, options, entityIsApi ? getApi(apis, pathParams.entity) : apis[0]));
        const { originalApi, apiHandler, loading, error: asyncParseError } = useCreateApiHandler(apiState.currentApi);
        const { t } = useTranslation();

        useEffect(() => {
          updateApiState(buildApiState(apis, options, entityIsApi ? getApi(apis, pathParams.entity) : apis[0]));
        }, [apis, options, entityIsApi, pathParams.entity]);

        useEffect(() => {
          try {
            let onReady = options.onReady;
            if (typeof onReady === 'string') {
              window[onReady]();
            } else if (typeof onReady === 'function') {
              onReady();
            }
          } catch (e) {
            console.error('onReady callback failed:', e);
          }
        }, [options.onReady]);

        useEffect(() => {
          if (entityIsApi) {
            const newApi = getApi(apis, pathParams.entity);

            // only call for new api if we know api path param has changed
            if (apiPath(apiState.currentApi) !== apiPath(newApi)) {
              updateApiState(buildApiState(apis, options, newApi));
            }
          }
        }, [pathParams, apis, pathParams.entity, apiState.currentApi, entityIsApi, options]);

        let jsx;

        if (loading) {
          jsx = <InlineLoading />;
        } else if (asyncParseError && !location.pathname.includes('parseerror')) {
          jsx = (
            <ToastNotification
              kind="error"
              title={t('async_parse_errors')}
              subtitle={t('async_parse_errors_description')}
              caption={
                <pre className="validationErrorText">
                  <code>{asyncParseError}</code>
                </pre>
              }
              className="errorFrame"
              hideCloseButton
            />
          );
        } else {
          jsx = <Routing apiState={apiState} />;
        }

        const shouldSplit = apiState.apiType !== 'GraphQL';

        return (
          <ApiHandlerProvider api={originalApi} handler={apiHandler} loading={loading} error={asyncParseError}>
            <ExplorerBody splitView={shouldSplit}>{jsx}</ExplorerBody>
          </ApiHandlerProvider>
        );
      }}
    />
  );
};

export default App;
